import React from 'react';
import {
    Box,
    Button,
    Typography,
} from '@mui/material';

const PaymentMethodsTab = ({
    cardsLoading,
    savedCards,
    handleOpenAddCard,
    handleSetPrimary,
    handleRemoveCard,
    settingPrimaryCardId,
    removingCardId,
    getCardBrandIcon,
}) => {
    return (
        <Box sx={{ width: '100%', px: { xs: '0', }, boxSizing: 'border-box' }}>
            <Box sx={{
                backgroundColor: 'var(--brand-surface, #fff)',
                border: '1px solid var(--brand-border, #e2e8f0)',
                borderRadius: '12px',
                p: { xs: 2, sm: 3 },
            }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--brand-text, #0f172a)' }}>Saved Cards</Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'var(--brand-text-muted, #64748b)', mt: 0.25 }}>Manage your cards used for billing</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleOpenAddCard}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            backgroundColor: '#FF6900',
                            color: '#fff',
                            borderRadius: '8px',
                            px: 2,
                            '&:hover': { backgroundColor: '#e55e00' }
                        }}
                    >
                        + Add Card
                    </Button>
                </Box>

                {/* Card List */}
                {cardsLoading ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ color: 'var(--brand-text-muted, #94a3b8)', fontSize: '0.875rem' }}>Loading cards...</Typography>
                    </Box>
                ) : savedCards.length === 0 ? (
                    <Box sx={{ py: 5, textAlign: 'center', backgroundColor: 'var(--brand-surface, #f8fafc)', borderRadius: '10px', border: '1px dashed var(--brand-border, #e2e8f0)' }}>
                        <Typography sx={{ fontSize: '2rem', mb: 1 }}>💳</Typography>
                        <Typography sx={{ color: 'var(--brand-text-muted, #64748b)', fontSize: '0.9rem', fontWeight: 600 }}>No saved cards yet</Typography>
                        <Typography sx={{ color: 'var(--brand-text-muted, #94a3b8)', fontSize: '0.8rem', mt: 0.5 }}>Add a card to enable automatic billing</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {savedCards.map((card) => (
                            <Box
                                key={card.payment_method_id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: '14px 18px',
                                    borderRadius: '10px',
                                    border: card.is_default ? '1.5px solid #FF6900' : '1px solid var(--brand-border, #e2e8f0)',
                                    backgroundColor: card.is_default ? 'rgba(255,105,0,0.06)' : 'var(--brand-surface, #fafafa)',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {/* Card Info */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '1.5rem' }}>{getCardBrandIcon(card.brand)}</Typography>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--brand-text, #0f172a)', textTransform: 'capitalize' }}>
                                                {card.brand} •••• {card.last4}
                                            </Typography>
                                            {card.is_default && (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.15, borderRadius: '4px', backgroundColor: 'rgba(255,105,0,0.1)' }}>
                                                    <Typography sx={{ color: '#FF6900', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase' }}>
                                                        Primary
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontSize: '0.775rem', color: 'var(--brand-text-muted, #64748b)', mt: 0.25 }}>
                                            Expires {card.exp_month?.toString().padStart(2, '0')}/{card.exp_year}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Actions */}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {!card.is_default && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleSetPrimary(card.payment_method_id)}
                                            disabled={settingPrimaryCardId === card.payment_method_id}
                                            sx={{
                                                textTransform: 'none', fontSize: '0.75rem', fontWeight: 600,
                                                borderRadius: '6px', borderColor: '#0097b2', color: '#0097b2', py: 0.4,
                                                '&:hover': { backgroundColor: 'rgba(0,151,178,0.08)', borderColor: '#0097b2' }
                                            }}
                                        >
                                            {settingPrimaryCardId === card.payment_method_id ? 'Setting...' : 'Set Primary'}
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleRemoveCard(card.payment_method_id)}
                                        disabled={removingCardId === card.payment_method_id || card.is_default}
                                        title={card.is_default ? 'Set another card as primary before removing this one.' : ''}
                                        sx={{
                                            textTransform: 'none', fontSize: '0.75rem', fontWeight: 600,
                                            borderRadius: '6px', borderColor: '#fecaca', color: '#ef4444', py: 0.4,
                                            '&:hover': { backgroundColor: 'rgba(239,68,68,0.06)', borderColor: '#ef4444' },
                                            '&.Mui-disabled': { borderColor: 'var(--brand-border, #e2e8f0)', color: 'var(--brand-text-muted, #cbd5e1)' },
                                        }}
                                    >
                                        {removingCardId === card.payment_method_id ? 'Removing...' : 'Remove'}
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PaymentMethodsTab;
